import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '') + '/api/v1';

// ===== TYPE DEFINITIONS =====
export interface AttractivenessScore {
  symbol: string;
  overall_score: number;
  breakdown: {
    technical: number;
    liquidity: number;
    signal: number;
  };
  recommendation: string;
  reasons: string[];
  current_price: number;
  timestamp: string;
  // Timeframe metadata
  recommendation_timeframe?: string;
  recommendation_type?: string;
  lookback_period?: string;
  data_start_date?: string;
  data_end_date?: string;
}

export interface ConsensusVote {
  feature: string;
  direction: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
  signal: string;
  confidence: number;
  short_reason: string;
}

export interface ConsensusData {
  symbol: string;
  votes: ConsensusVote[];
  bullish_count: number;
  bearish_count: number;
  neutral_count: number;
  agreement: 'STRONG_BULL' | 'BULL' | 'SPLIT' | 'BEAR' | 'STRONG_BEAR' | 'NEUTRAL' | string;
  agreement_label: string;
  timestamp: string;
}

export interface VolatilityRange {
  symbol: string;
  current_price: number;
  ranges: {
    [key: string]: {
      method: string;
      lower: number;
      middle?: number;
      upper: number;
      confidence: number;
    };
  };
  current_zone: string;
  recommendation: {
    action: string;
    target_price: number | null;
    expected_return_pct: number;
    stop_loss: number | null;
    range_lower: number;
    range_upper: number;
    reason: string;
  };
  timestamp: string;
  // Timeframe metadata
  lookback_period?: string;
  data_start_date?: string;
  data_end_date?: string;
  actual_range_low?: number;
  actual_range_high?: number;
  recommendation_validity?: string;
  // Tested forward ranges (Feature 43 §14) and how this page's range and zones tested (Feature 02)
  forward_ranges?: { horizon: number; lower: number; upper: number; lower_pct: number; upper_pct: number;
    group: string; relation: string; tested?: LikelyRangeTest | null }[];
  page_test?: {
    lookback: string; dates: number; median_width_pct: number; stayed_inside_next_week_pct: number;
    [k: string]: any;
    zones: Record<string, { share_pct: number; next_month_vs_average_pct: number; q?: number; halves: number[] }>;
  } | null;
}

export interface MLPrediction {
  symbol: string;
  prediction_days: number;
  current_price: number;
  predicted_price: number;
  predicted_change_pct: number;
  prediction_lower: number;
  prediction_upper: number;
  confidence: number;
  timestamp: string;
  prediction_series?: Array<{
    day: number;
    price: number;
  }>;
  data_start_date?: string;
  data_end_date?: string;
  data_points?: number;
  model_type?: string;
  // What the run actually used (XGBoost: the model's feature columns; Chronos-2: covariates)
  features_used?: string[];
  tickers_used?: string[];
  skipped?: string[];
  // XGBoost only: what the loaded model was trained on
  trained_at?: string;
  training_start?: string;
  training_end?: string;
  training_samples?: number;
  input_date?: string;
  // Chronos-2 only: its range stretched to what it held in testing (Feature 43)
  likely_range?: LikelyRange | null;
}

export interface LikelyRangeTest {
  horizon: number;
  dates?: number;
  from?: string;
  to?: string;
  held?: number;
  below?: number;
  above?: number;
  halves?: Record<string, number>;
  other_held?: number;            // the range not shown, same test
  chronos_raw_held?: number;      // Chronos-2's own range before the correction
  chronos_better_dates?: number;  // dates Chronos-2's corrected range scored better than the volatility one
  comparison_dates?: number;
  q?: number;
  median_width_pct?: { chronos: number; vol: number };
  group_chronos_better_dates?: number;  // the same over all tested horizons (decides the range shown)
  group_dates?: number;
}

export interface LikelyRange {
  group: string;
  // 'tested': the symbol was in the test; 'untested_etf' / 'untested_stock': its group's correction applies
  relation: 'tested' | 'untested_etf' | 'untested_stock';
  horizon: number;
  method: 'chronos' | 'vol';      // the range shown: the one that tested better for this group
  raw_lower: number;              // Chronos-2's own 10–90% range
  raw_upper: number;
  lower: number | null;           // null past the longest tested horizon
  upper: number | null;
  lower_pct?: number;
  upper_pct?: number;
  chronos: { lower: number; upper: number } | null;
  vol: { lower: number; upper: number } | null;
  annual_vol: number | null;
  series: Array<{ day: number; lower: number; upper: number }>;
  tested: LikelyRangeTest | null;
}

// Feature 46 — what the holdings could lose, how they move with SPY, where the money is concentrated
export interface PortfolioHolding {
  symbol: string; shares: number; avg_cost: number | null; price: number | null; value: number; stale: boolean;
  sector: string; name?: string | null; gain_pct: number | null; weight: number; risk_share?: number;
  annual_vol?: number; bad_month_price?: number; bad_month_dollars?: number;
}

// Feature 36 — Catalyst Chains, shadow mode
export interface CatalystCandidate {
  symbol: string; stage: string; score: number; evidence_count: number; evidence: any[]; ret_6m: number | null; price: number | null;
}

export interface CatalystData {
  as_of: string | null; snapshot_days?: number; phase_a?: any;
  needs: { need_id: string; name: string; kind?: string; strength: number; articles_7d: number; outlets_7d: number;
    weekly_median: number; breadth: number | null; members: number; headlines: any[]; candidates: CatalystCandidate[] }[];
}

// Feature 50 — every claim a page makes, its test and the verdict
export interface EvidenceEntry {
  id: string; pages: string[]; verdict: 'proven' | 'promising' | 'no_edge' | 'harmful' | 'too_early';
  claim: string; test: string; result: string; doc: string;
}

export interface EvidenceData {
  entries: EvidenceEntry[];
  verdicts: Record<string, string>;
  pages: Record<string, Record<string, number>>;
}

// Feature 48 — predictions scored against how often the move happens anyway
export interface MarketCall {
  id: number; source: string; target: string; direction: 'up' | 'down'; move_pct: number; horizon_days: number;
  rule: 'touches' | 'ends'; made_on: string; note: string | null; start_price: number | null; base_rate: number | null;
  status: 'open' | 'hit' | 'miss'; resolved_on: string | null; outcome_pct: number | null;
}

export interface CallTrackerData {
  calls: MarketCall[];
  sources: Record<string, { calls: number; settled: number; hits: number; expected_hits: number; hit_rate: number | null;
    base_rate: number | null; p_better_than_chance: number | null; verdict: string | null }>;
}

export interface HoldingAlert { symbol: string; kind: string; message: string; created_at: string; sent: boolean }

export interface PortfolioRisk {
  holdings: PortfolioHolding[];
  summary: null | {
    value: number; as_of: string; holdings: number; effective_holdings: number; largest_weight: number;
    top3_weight: number; share_with_a_year_of_prices: number;
    bad_month?: { pct: number; dollars: number }; very_bad_month?: { pct: number; dollars: number };
    bad_quarter?: { pct: number; dollars: number };
    annual_vol?: number; spy_annual_vol?: number; beta?: number; correlation?: number;
    sectors: Record<string, number>;
    falls: { label: string; from: string; to: string; covered: number; portfolio_pct: number | null; spy_pct: number }[];
  };
  message?: string;
}

// Feature 44 — every stock-naming page's new picks, scored against SPY and same-volatility stocks
export interface ScoreboardCell {
  matured: number;
  weeks?: number;
  avg_return_pct?: number | null;
  vs_spy_pct?: number | null;
  median_vs_spy_pct?: number | null;
  vs_band_pct?: number | null;
  beat_band_pct?: number | null;
  beat_band_base_pct?: number | null;
  fell20_pct?: number | null;
  fell20_base_pct?: number | null;
  backfilled_share_pct?: number | null;
  t?: number | null;
  p?: number | null;
  q?: number | null;
}

export interface Scoreboard {
  generated_at: string;
  new_after_days: number;
  horizons: Record<string, number>;
  sources: Record<string, { label: string; new_picks: number; first_pick: string | null; horizons: Record<string, ScoreboardCell> }>;
}

export interface LikelyRangeReliability {
  groups: Record<string, {
    use: 'chronos' | 'vol';
    horizons: Record<string, { chronos_held?: number; vol_held?: number; chronos_better_dates?: number; dates?: number; q?: number }>;
  }>;
  group_use: Record<string, { use: 'chronos' | 'vol'; chronos_better_dates: number; dates: number }>;
  etf_classes: Record<string, string[]>;
}

export interface TechnicalIndicators {
  symbol: string;
  data: Array<{
    date: string;
    close: number;
    rsi?: number;
    macd?: number;
    macd_signal?: number;
    macd_hist?: number;
    bb_upper?: number;
    bb_middle?: number;
    bb_lower?: number;
    [key: string]: any;
  }>;
}

export interface TradingSignal {
  symbol: string;
  latest_signals: {
    date: string;
    close: number;
    buy_signal: boolean;
    sell_signal: boolean;
    rsi?: number;
    macd?: number;
    bb_position?: string;
  };
  buy_signals: Array<{
    date: string;
    close: number;
    signal_type: string;
    [key: string]: any;
  }>;
  sell_signals: Array<{
    date: string;
    close: number;
    signal_type: string;
    [key: string]: any;
  }>;
  // Timeframe metadata
  signal_validity_days?: number;
  lookback_period?: string;
  signal_strength?: number;  // 0-10 scale
  data_start_date?: string;
  data_end_date?: string;
  // Price targets
  target_price?: number;
  expected_return_pct?: number;
  target_timeframe?: string;
  stop_loss?: number;
}

export interface IBKRStatus {
  connected: boolean;
  server_version?: number;
  connection_time?: string;
  accounts?: string[];
}

export interface PatternMatch {
  start_date: string;
  end_date: string;
  match_score: number;
  dtw_score: number;
  matrix_profile_score: number;
  sax_score: number;
  pla_score: number;
  cross_correlation_score: number;
  pattern_data: number[];
  future_pattern: number[] | null;
  future_price_change: {
    min_change: number;
    max_change: number;
    final_change: number;
    days_forward: number;
  } | null;
  start_price: number;
  end_price: number;
}

export interface PatternMatchResult {
  symbol: string;
  current_pattern: number[];
  current_start_date: string;
  current_end_date: string;
  window_days: number;
  matches: PatternMatch[];
  statistics: {
    avg_future_change?: number;
    median_future_change?: number;
    std_future_change?: number;
    min_future_change?: number;
    max_future_change?: number;
    bullish_outcomes?: number;
    bearish_outcomes?: number;
    total_matches: number;
    avg_match_score: number;
    no_future_data?: boolean;
  };
  method: string;
}

// ===== TRADING SYSTEM =====
export interface TradingBucket {
  id: number;
  name: string;
  strategy_type: string;
  allocated_capital: number;
  available_capital: number;
  capital_utilized: number;
  max_positions: number;
  max_position_size: number;
  stop_loss_pct?: number;
  take_profit_pct?: number;
  daily_loss_limit?: number;
  total_loss_limit?: number;
  mode: string;  // 'paper' or 'live'
  status: string;  // 'active', 'paused', 'disabled'
  auto_trading_enabled: boolean;  // Whether auto-trading is enabled for this bucket
  open_positions: number;
  total_positions: number;
  total_pnl: number;
  win_rate?: number;
  sharpe_ratio?: number;
  max_drawdown?: number;
  created_at: string;
  updated_at: string;
}

export interface Position {
  id: number;
  bucket_id: number;
  symbol: string;
  direction: string;  // 'long' or 'short'
  quantity: number;
  entry_price: number;
  current_price?: number;
  target_price?: number;
  stop_loss_price?: number;
  unrealized_pnl?: number;
  unrealized_pnl_pct?: number;
  entry_cost: number;
  commission_paid: number;
  market_value: number;
  status: string;  // 'open' or 'closed'
  is_open: boolean;
  is_long: boolean;
  is_short: boolean;
  days_open: number;
  opened_at: string;
  closed_at?: string;
  updated_at: string;
  entry_reason?: string;
  exit_reason?: string;
  metadata?: any;
}

export interface DashboardSummary {
  summary: {
    total_buckets: number;
    total_allocated_capital: number;
    total_available_capital: number;
    capital_utilization_pct: number;
    total_open_positions: number;
    total_unrealized_pnl: number;
  };
  top_buckets: Array<{
    bucket_id: number;
    bucket_name: string;
    strategy_type: string;
    total_pnl: number;
    open_positions: number;
  }>;
  recent_positions: Position[];
}

export interface BucketCreate {
  name: string;
  strategy_type: string;
  allocated_capital: number;
  max_positions?: number;
  max_position_size: number;
  stop_loss_pct?: number;
  take_profit_pct?: number;
  daily_loss_limit?: number;
  total_loss_limit?: number;
  mode?: string;
}

// ===== AUTO-TRADING =====
export interface AutoProcessResults {
  status: 'success' | 'error';
  results: {
    opportunities_evaluated: number;
    positions_opened: number;
    positions_skipped: number;
    errors: string[];
  };
}

export interface AutoMonitorResults {
  status: 'success' | 'error';
  results: {
    positions_monitored: number;
    positions_closed: number;
    positions_updated: number;
    errors: string[];
  };
}

export interface AutoTradingStatus {
  summary: {
    total_buckets: number;
    auto_trading_enabled_buckets: number;
    total_available_opportunities: number;
    total_monitored_positions: number;
  };
  buckets: Array<{
    bucket_id: number;
    bucket_name: string;
    auto_trading_enabled: boolean;
    available_opportunities: number;
    open_positions: number;
    available_capital: number;
  }>;
  timestamp: string;
}

// ===== MARKET POSITION =====
export interface RangeAnalysis {
  range_days: number;
  range_high: number;
  range_low: number;
  range_size: number;
  position_pct: number;
  position: 'top' | 'bottom' | 'neutral';
  position_label: string;
  distance_to_high: number;
  distance_to_high_pct: number;
  distance_to_low: number;
  distance_to_low_pct: number;
  position_change?: {
    days_ago: number;
    previous_position_pct: number | null;
    current_position_pct: number;
    change_pct: number | null;
    trend: string;
  };
  volume_analysis?: {
    avg_volume: number;
    recent_avg_volume: number;
    volume_ratio: number;
    volume_status: string;
    volume_label: string;
    interpretation: string;
    signal: string;
  };
  breakout_status?: {
    status: string;
    description: string;
    direction: string;
    strength_pct: number;
    is_new_breakout: boolean;
  };
  key_levels?: {
    support_levels: number[];
    resistance_levels: number[];
    num_support: number;
    num_resistance: number;
  };
}

export interface OverallPosition {
  stance: string;
  combined_position_pct: number;
  recommendation: string;
  signal: 'BUY' | 'SELL' | 'HOLD';
}

export interface NasdaqAnalysis {
  symbol: string;
  current_price: number;
  local: RangeAnalysis;
  global: RangeAnalysis;
  overall_position: OverallPosition;
}

export interface RelativeToMarket {
  relative_strength: 'outperforming' | 'underperforming' | 'in_line';
  position_difference: number;
  interpretation: string;
}

export interface MarketPosition {
  symbol: string;
  current_price: number;
  as_of_date: string;
  local: RangeAnalysis;
  global: RangeAnalysis;
  overall_position: OverallPosition;
  nasdaq?: NasdaqAnalysis;
  relative_to_market?: RelativeToMarket;
  timestamp: string;
}

// ===== VOLATILITY SCANNER =====
export interface VolatilityOpportunity {
  symbol: string;
  current_price: number;
  range: {
    low: number;
    high: number;
    midpoint: number;
  };
  position: {
    in_range_pct: number;
    from_low_pct: number;
    from_high_pct: number;
  };
  recommendation: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  method: string;
  volatility?: number;
  volume?: number;
  scan_date: string;
}

export interface VolatilityOpportunitiesResponse {
  scan_date: string;
  recommendation: 'BUY' | 'SELL';
  count: number;
  opportunities: VolatilityOpportunity[];
}

export interface VolatilityQuickScanResponse {
  scan_date: string;
  scanned: number;
  successful: number;
  buy_count: number;
  sell_count: number;
  buy_opportunities: VolatilityOpportunity[];
  sell_opportunities: VolatilityOpportunity[];
}

export interface VolatilityScanStatus {
  status: string;
  scan_date: string;
  total_stocks: number;
  scanned?: number;
  successful?: number;
  failed?: number;
  buy_opportunities: number;
  sell_opportunities: number;
  hold_count?: number;
}

// ===== COMPREHENSIVE SCORE =====
export interface ComprehensiveNewsInsight {
  composite_score: number;
  direction: 'bullish' | 'bearish' | 'neutral';
  signal_count: number;
  article_count: number;
  avg_confidence: number;
  dominant_category: string | null;
  hours_window: number;
}

export interface ComprehensivePriceTrend {
  current_price: number;
  change_3d_pct: number;
  change_1d_pct: number;
  trend: 'up' | 'down' | 'flat';
}

export interface ComprehensiveDivergence {
  has_divergence: boolean;
  divergence_type: 'bearish_divergence' | 'bullish_divergence' | 'aligned_bullish' | 'aligned_bearish' | 'neutral';
  caution_level: 'high' | 'medium' | 'low' | 'none';
  interpretation: string;
}

export interface ComprehensiveScore {
  symbol: string;
  comprehensive_score: number;
  attractiveness_score: number;
  news_score: number;
  alignment_score: number;
  recommendation: string;
  original_recommendation: string;
  news: ComprehensiveNewsInsight;
  price_trend: ComprehensivePriceTrend;
  divergence: ComprehensiveDivergence;
  key_insights: string[];
  timestamp: string;
}

// ===== TRADING RECOMMENDATIONS =====
export interface Recommendation {
  id: number;
  bucket_id?: number;
  symbol: string;
  direction: 'long' | 'short';
  current_price: number;
  target_price: number;
  stop_loss_price: number;
  expected_return_pct: number;
  risk_reward_ratio: number;
  confidence_score: number;
  reason: string;
  strategy_data: any;
  acted_upon: boolean;
  position_id?: number;
  is_expired: boolean;
  is_available: boolean;
  scanned_at: string;
  expires_at?: string;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 300000, // 5 minutes for IBKR data fetching
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - Add JWT token to all requests
    this.client.interceptors.request.use(
      config => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      response => response,
      error => {
        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('username');
          window.location.href = '/login';
        }
        // Normalise FastAPI validation errors before any page sees them.
        //
        // A 422 carries `detail` as an ARRAY of {loc, msg, type} objects. Eighteen pages
        // do `setError(err.response?.data?.detail || '...')` and then render `{error}` as
        // a React child — and React cannot render an array of objects, so the throw
        // unmounted the whole tree and the user saw a blank white page instead of the
        // message. Pattern Matching hit it the moment a 63-day window met a 60-day cap.
        // Flattening it here fixes every page at once and keeps the raw list on
        // `detail_raw` for anything that wants the structure.
        const d = error.response?.data;
        if (d && Array.isArray(d.detail)) {
          d.detail_raw = d.detail;
          d.detail = d.detail
            .map((e: any) => {
              const where = Array.isArray(e?.loc) ? e.loc.filter((x: any) => x !== 'body').join('.') : '';
              return where ? `${where}: ${e?.msg ?? JSON.stringify(e)}` : (e?.msg ?? JSON.stringify(e));
            })
            .join('; ');
        } else if (d && d.detail && typeof d.detail === 'object') {
          d.detail_raw = d.detail;
          d.detail = JSON.stringify(d.detail);
        }
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // ===== STOCK SCORING =====
  async getAttractivenessScore(symbol: string, duration: string = '6 M'): Promise<AttractivenessScore> {
    const response = await this.client.get(`/scoring/attractiveness/${symbol}`, {
      params: { duration },
    });
    return response.data;
  }

  async batchScoreStocks(symbols: string[], duration: string = '6 M', limit: number = 10) {
    // Build query string manually to handle array parameters
    const params = new URLSearchParams();
    symbols.forEach(symbol => params.append('symbols', symbol));
    params.append('duration', duration);
    params.append('limit', limit.toString());

    const response = await this.client.post(`/scoring/batch?${params.toString()}`);
    return response.data;
  }

  async getTopAttractiveStocks(symbols: string[], limit: number = 10) {
    // Build query string manually to handle array parameters
    const params = new URLSearchParams();
    symbols.forEach(symbol => params.append('symbols', symbol));
    params.append('limit', limit.toString());

    const response = await this.client.get(`/scoring/top?${params.toString()}`);
    return response.data;
  }

  async getConsensus(symbol: string): Promise<ConsensusData> {
    const response = await this.client.get(`/scoring/consensus/${symbol}`);
    return response.data;
  }

  // ===== VOLATILITY RANGES =====
  async getTradingRanges(
    symbol: string,
    duration: string = '6 M',
    method: string = 'all'
  ): Promise<VolatilityRange> {
    const response = await this.client.get(`/ranges/${symbol}`, {
      params: { duration, method },
    });
    return response.data;
  }

  async getHistoricalAccuracy(symbol: string, duration: string = '1 Y', lookback_days: number = 60) {
    const response = await this.client.get(`/ranges/${symbol}/historical`, {
      params: { duration, lookback_days },
    });
    return response.data;
  }

  // ===== ML PREDICTIONS =====
  async trainModel(
    symbol: string,
    prediction_days: number = 10,
    duration: string = '1 Y',
    features?: string[],
    additional_tickers?: string[]
  ) {
    const response = await this.client.post('/predictions/train', {
      symbol,
      prediction_days,
      duration,
      features,
      additional_tickers,
    });
    return response.data;
  }

  async getMLPrediction(
    symbol: string,
    prediction_days: number = 10,
    additional_tickers?: string[],
    features?: string[]
  ): Promise<MLPrediction> {
    // The backend reuses a saved model only if it was trained with these tickers and features
    const response = await this.client.post('/predictions/predict', {
      symbol,
      prediction_days,
      additional_tickers,
      features,
    });
    return response.data;
  }

  async getChronos2Prediction(
    symbol: string,
    prediction_days: number = 10,
    covariates?: string[],
    additional_tickers?: string[]
  ): Promise<MLPrediction> {
    const response = await this.client.post('/predictions/predict-chronos2', {
      symbol,
      prediction_days,
      covariates,
      additional_tickers,
    });
    return response.data;
  }

  async getLikelyRangeReliability(): Promise<LikelyRangeReliability> {
    const response = await this.client.get('/predictions/likely-range/reliability');
    return response.data;
  }

  async getPortfolioRisk(): Promise<PortfolioRisk> {
    const response = await this.client.get('/portfolio-risk', { timeout: 120000 });
    return response.data;
  }

  async addHolding(symbol: string, shares: number, avg_cost?: number): Promise<any> {
    const response = await this.client.post('/portfolio-risk/holdings', { symbol, shares, avg_cost });
    return response.data;
  }

  async removeHolding(symbol: string): Promise<any> {
    const response = await this.client.delete(`/portfolio-risk/holdings/${encodeURIComponent(symbol)}`);
    return response.data;
  }

  async importHoldings(source: 'journal' | 'ibkr'): Promise<{ imported: number }> {
    const response = await this.client.post(`/portfolio-risk/import/${source}`, null, { timeout: 60000 });
    return response.data;
  }

  async getHoldingAlerts(): Promise<HoldingAlert[]> {
    const response = await this.client.get('/portfolio-risk/alerts');
    return response.data;
  }

  async checkHoldingAlerts(): Promise<{ new: number; alerts: HoldingAlert[] }> {
    const response = await this.client.post('/portfolio-risk/alerts/check', null, { timeout: 180000 });
    return response.data;
  }

  async getCalls(): Promise<CallTrackerData> {
    const response = await this.client.get('/calls');
    return response.data;
  }

  async addCall(call: Record<string, any>): Promise<CallTrackerData> {
    const response = await this.client.post('/calls', call);
    return response.data;
  }

  async deleteCall(id: number): Promise<CallTrackerData> {
    const response = await this.client.delete(`/calls/${id}`);
    return response.data;
  }

  async getCallBaseRate(target: string, direction: string, move_pct: number, horizon_days: number, rule: string):
    Promise<{ base_rate: number | null; start_dates: number }> {
    const response = await this.client.get('/calls/base-rate', { params: { target, direction, move_pct, horizon_days, rule } });
    return response.data;
  }

  async getEarnings(symbol: string): Promise<any> {
    const response = await this.client.get(`/earnings/${encodeURIComponent(symbol)}`, { timeout: 120000 });
    return response.data;
  }

  async getUpcomingEarnings(): Promise<any> {
    const response = await this.client.get('/earnings/upcoming', { timeout: 180000 });
    return response.data;
  }

  async getCatalysts(): Promise<CatalystData> {
    const response = await this.client.get('/catalysts');
    return response.data;
  }

  async getCatalystDrivers(days = 7): Promise<any> {
    const response = await this.client.get('/catalysts/drivers', { params: { days } });
    return response.data;
  }

  async runCatalysts(): Promise<CatalystData> {
    const response = await this.client.post('/catalysts/run', null, { timeout: 600000 });
    return response.data;
  }

  async getEvidence(): Promise<EvidenceData> {
    const response = await this.client.get('/evidence');
    return response.data;
  }

  // Signal Lab (Feature 53) and the tax check (Feature 54)
  async getSignalLab(): Promise<any> {
    const response = await this.client.get('/signal-lab', { timeout: 60000 });
    return response.data;
  }

  async getEvidencePortfolio(): Promise<any> {
    const response = await this.client.get('/signal-lab/evidence-portfolio', { timeout: 60000 });
    return response.data;
  }

  async getTaxCheck(): Promise<any> {
    const response = await this.client.get('/portfolio-risk/tax-check', { timeout: 120000 });
    return response.data;
  }

  async getScoreboard(): Promise<Scoreboard> {
    const response = await this.client.get('/scoreboard');
    return response.data;
  }

  async refreshScoreboard(): Promise<Scoreboard> {
    const response = await this.client.post('/scoreboard/refresh', null, { timeout: 300000 });
    return response.data;
  }

  async listTrainedModels() {
    const response = await this.client.get('/predictions/models');
    return response.data;
  }

  async deleteModel(symbol: string, prediction_days: number) {
    const response = await this.client.delete(`/predictions/models/${symbol}/${prediction_days}`);
    return response.data;
  }

  // ===== TECHNICAL INDICATORS =====
  async getTechnicalIndicators(symbol: string, duration: string = '6 M'): Promise<TechnicalIndicators> {
    const response = await this.client.get(`/predictions/indicators/${symbol}`, {
      params: { duration },
    });
    return response.data;
  }

  async getTradingSignals(symbol: string, duration: string = '6 M'): Promise<TradingSignal> {
    const response = await this.client.get(`/predictions/signals/${symbol}`, {
      params: { duration },
    });
    return response.data;
  }

  // ===== IBKR =====
  async connectIBKR() {
    const response = await this.client.post('/ibkr/connect');
    return response.data;
  }

  async getIBKRStatus(): Promise<IBKRStatus> {
    const response = await this.client.get('/ibkr/status');
    return response.data;
  }

  async getHistoricalData(symbol: string, duration: string = '1 M', bar_size: string = '1 day') {
    const response = await this.client.post('/ibkr/historical-data', {
      symbol,
      duration,
      bar_size,
      what_to_show: 'TRADES',
    });
    return response.data;
  }

  async getContract(symbol: string) {
    const response = await this.client.get(`/ibkr/contract/${symbol}`);
    return response.data;
  }

  async getTicker(symbol: string) {
    const response = await this.client.get(`/ibkr/ticker/${symbol}`);
    return response.data;
  }

  // Market scanners
  async getTopGainers(limit: number = 20) {
    const response = await this.client.get('/ibkr/scan/top-gainers', {
      params: { num_rows: limit },
    });
    return response.data;
  }

  async getTopLosers(limit: number = 20) {
    const response = await this.client.get('/ibkr/scan/top-losers', {
      params: { num_rows: limit },
    });
    return response.data;
  }

  async getMostActive(limit: number = 20) {
    const response = await this.client.get('/ibkr/scan/most-active', {
      params: { num_rows: limit },
    });
    return response.data;
  }

  // ===== PATTERN MATCHING =====
  async findSimilarPatterns(
    symbol: string,
    window_days: number = 5,
    method: string = 'all',
    top_n: number = 10,
    history_years: number = 10,
    prediction_days: number = 21
  ): Promise<PatternMatchResult> {
    const response = await this.client.post('/patterns/find', {
      symbol,
      window_days,
      method,
      top_n,
      history_years,
      prediction_days,
    });
    return response.data;
  }

  // ===== TRADING SYSTEM =====
  async getDashboardSummary(): Promise<DashboardSummary> {
    const response = await this.client.get('/trading/dashboard');
    return response.data;
  }

  async listBuckets(status?: string, mode?: string): Promise<TradingBucket[]> {
    const params: any = {};
    if (status) params.status = status;
    if (mode) params.mode = mode;
    const response = await this.client.get('/trading/buckets', { params });
    return response.data;
  }

  async getBucket(bucketId: number): Promise<TradingBucket> {
    const response = await this.client.get(`/trading/buckets/${bucketId}`);
    return response.data;
  }

  async createBucket(bucket: BucketCreate): Promise<TradingBucket> {
    const response = await this.client.post('/trading/buckets', bucket);
    return response.data;
  }

  async updateBucket(bucketId: number, updates: Partial<BucketCreate>): Promise<TradingBucket> {
    const response = await this.client.patch(`/trading/buckets/${bucketId}`, updates);
    return response.data;
  }

  async deleteBucket(bucketId: number): Promise<void> {
    await this.client.delete(`/trading/buckets/${bucketId}`);
  }

  async listPositions(
    bucketId?: number,
    status?: string,
    symbol?: string
  ): Promise<Position[]> {
    const params: any = {};
    if (bucketId) params.bucket_id = bucketId;
    if (status) params.status = status;
    if (symbol) params.symbol = symbol;
    const response = await this.client.get('/trading/positions', { params });
    return response.data;
  }

  async getPosition(positionId: number): Promise<Position> {
    const response = await this.client.get(`/trading/positions/${positionId}`);
    return response.data;
  }

  async getBucketPositions(bucketId: number, status?: string): Promise<Position[]> {
    const params: any = {};
    if (status) params.status = status;
    const response = await this.client.get(`/trading/buckets/${bucketId}/positions`, { params });
    return response.data;
  }

  // ===== AUTO-TRADING =====
  async autoProcessOpportunities(bucketId?: number, minConfidence: number = 0.7): Promise<AutoProcessResults> {
    const response = await this.client.post('/trading/auto/process-opportunities', {
      bucket_id: bucketId,
      min_confidence: minConfidence,
    });
    return response.data;
  }

  async autoMonitorPositions(bucketId?: number): Promise<AutoMonitorResults> {
    const response = await this.client.post('/trading/auto/monitor-positions', {
      bucket_id: bucketId,
    });
    return response.data;
  }

  async enableAutoTrading(bucketId: number) {
    const response = await this.client.post(`/trading/buckets/${bucketId}/auto/enable`);
    return response.data;
  }

  async disableAutoTrading(bucketId: number) {
    const response = await this.client.post(`/trading/buckets/${bucketId}/auto/disable`);
    return response.data;
  }

  async getAutoTradingStatus(): Promise<AutoTradingStatus> {
    const response = await this.client.get('/trading/auto/status');
    return response.data;
  }

  // ===== MARKET POSITION =====
  async getMarketPosition(
    symbol: string,
    includeNasdaq: boolean = true,
    nasdaqSymbol: string = 'QQQ'
  ): Promise<MarketPosition> {
    const response = await this.client.get(`/market-position/${symbol}`, {
      params: {
        include_nasdaq: includeNasdaq,
        nasdaq_symbol: nasdaqSymbol,
      },
    });
    return response.data;
  }

  // ===== VOLATILITY SCANNER =====
  async getVolatilityOpportunities(
    recommendation: 'BUY' | 'SELL' = 'BUY',
    limit: number = 10,
    scanDate?: string
  ): Promise<VolatilityOpportunitiesResponse> {
    const params: any = { recommendation, limit };
    if (scanDate) params.scan_date = scanDate;
    const response = await this.client.get('/volatility-scanner/top-opportunities', { params });
    return response.data;
  }

  async getLowRangeStocks(limit = 1000): Promise<any> {
    // 1000, not the old default of 100. After a broad selloff the BUY zone holds many
    // hundreds of names; a 100-row cap sorted most-oversold-first showed only the
    // breakdowns and hid every stock genuinely sitting at the bottom of its range.
    const response = await this.client.get('/volatility-scanner/low-range-stocks',
      { params: { limit }, timeout: 60000 });
    return response.data;
  }

  async runVolatilityQuickScan(maxStocks: number = 100): Promise<VolatilityQuickScanResponse> {
    const response = await this.client.post('/volatility-scanner/quick-scan', null, {
      params: { max_stocks: maxStocks },
      timeout: 180000, // 3 minutes
    });
    return response.data;
  }

  async triggerVolatilityScan(maxConcurrent: number = 10): Promise<VolatilityScanStatus> {
    const response = await this.client.post('/volatility-scanner/scan', null, {
      params: { max_concurrent: maxConcurrent },
      timeout: 600000, // 10 minutes for full NASDAQ scan
    });
    return response.data;
  }

  async getVolatilityScanStatus(scanDate?: string): Promise<VolatilityScanStatus> {
    const params: any = {};
    if (scanDate) params.scan_date = scanDate;
    const response = await this.client.get('/volatility-scanner/scan-status', { params });
    return response.data;
  }

  // ===== TRADING RECOMMENDATIONS =====
  async generateRecommendation(symbol: string, bucketId?: number) {
    const response = await this.client.post('/recommendations/generate', {
      symbol,
      bucket_id: bucketId,
    });
    return response.data;
  }

  async scanForRecommendations(symbols: string[], bucketId?: number, minConfidence: number = 0.6) {
    const response = await this.client.post('/recommendations/scan', {
      symbols,
      bucket_id: bucketId,
      min_confidence: minConfidence,
    });
    return response.data;
  }

  async scanAllStocks(bucketId?: number, minConfidence: number = 0.6, limit: number = 20, useScanner: boolean = false) {
    const params: any = { min_confidence: minConfidence, limit, use_scanner: useScanner };
    if (bucketId) params.bucket_id = bucketId;
    const response = await this.client.post('/recommendations/scan-all', null, { params, timeout: 300000 }); // 5 min timeout
    return response.data;
  }

  async listRecommendations(bucketId?: number, availableOnly: boolean = true, limit: number = 50) {
    const params: any = { limit, available_only: availableOnly };
    if (bucketId) params.bucket_id = bucketId;
    const response = await this.client.get('/recommendations/list', { params });
    return response.data;
  }

  async getRecommendation(opportunityId: number) {
    const response = await this.client.get(`/recommendations/${opportunityId}`);
    return response.data;
  }

  async approveRecommendation(opportunityId: number, quantity: number, notes?: string) {
    const response = await this.client.post('/recommendations/approve', {
      opportunity_id: opportunityId,
      quantity,
      notes,
    });
    return response.data;
  }

  async deleteRecommendation(opportunityId: number) {
    const response = await this.client.delete(`/recommendations/${opportunityId}`);
    return response.data;
  }

  // ── News Signals ────────────────────────────────────────────────────────
  async getNewsFeed(hours = 24, limit = 50) {
    const response = await this.client.get('/signals/feed', { params: { hours, limit } });
    return response.data;
  }

  async getNotRelevantFeed(hours = 24, limit = 100) {
    const response = await this.client.get('/signals/not-relevant', { params: { hours, limit } });
    return response.data;
  }

  async getAggregatedSignals(hours = 24, minArticles = 2, actionableOnly = true) {
    const response = await this.client.get('/signals/aggregated', {
      params: { hours, min_articles: minArticles, actionable_only: actionableOnly },
    });
    return response.data;
  }

  async getSignalsStatus() {
    const response = await this.client.get('/signals/status');
    return response.data;
  }

  async getStockSignals(symbol: string, hours = 24) {
    const response = await this.client.get(`/signals/stock/${symbol}`, { params: { hours } });
    return response.data;
  }

  async getSignalsHeatmap(hours = 24) {
    const response = await this.client.get('/signals/heatmap', { params: { hours } });
    return response.data;
  }

  async getSignalsAccuracy() {
    const response = await this.client.get('/signals/accuracy');
    return response.data;
  }

  async compareModelSignals(symbol: string, hours = 48) {
    const response = await this.client.get(`/signals/compare/${symbol}`, { params: { hours } });
    return response.data;
  }

  async refreshSignals() {
    const response = await this.client.post('/signals/refresh');
    return response.data;
  }

  async getStockMeta(): Promise<Record<string, { name: string | null; sector: string | null }>> {
    const response = await this.client.get('/signals/stock-meta');
    return response.data;
  }

  async getTickerInfo(symbols: string[]): Promise<Record<string, { name: string; sector: string }>> {
    if (!symbols.length) return {};
    const response = await this.client.get('/scoring/ticker-info', {
      params: { symbols: symbols.join(',') },
    });
    return response.data;
  }

  async getComprehensiveScore(
    symbol: string,
    attractivenessScore: number = 50,
    recommendation: string = 'HOLD',
    newsHours: number = 72,
  ): Promise<ComprehensiveScore> {
    const response = await this.client.get(`/scoring/comprehensive/${symbol}`, {
      params: {
        attractiveness_score: attractivenessScore,
        recommendation,
        news_hours: newsHours,
      },
    });
    return response.data;
  }

  // ── Market Prices (yfinance poller) ────────────────────────────────────────
  async getAllPrices(): Promise<Record<string, { symbol: string; price: number; prev_close: number; change_pct: number; volume: number; updated_at: string; source: string }>> {
    const response = await this.client.get('/prices/');
    return response.data;
  }

  // ── Market Valuation Indicators ───────────────────────────────────────────
  async getMarketValuation(): Promise<{
    us_buffett: { value: number | null; label: string | null; severity: string | null; description: string; market_cap_trillions: number | null; gdp_trillions: number | null; historical_mean_pct: number };
    global_buffett: { value: number | null; label: string | null; severity: string | null; description: string; world_gdp_trillions: number | null; world_gdp_year: string | null; historical_mean_pct: number };
    shiller_cape: { value: number | null; label: string | null; severity: string | null; data_date: string | null; description: string; historical_mean: number };
    sp500_trailing_pe: { value: number | null; label: string | null; severity: string | null; sp500_price: number | null; description: string; historical_mean: number };
    fetched_at: string;
  }> {
    const response = await this.client.get('/market/valuation');
    return response.data;
  }

  async getCollapseRadar(): Promise<any> {
    const response = await this.client.get('/market/collapse-radar');
    return response.data;
  }

  async getVolMeter(): Promise<any> {
    const response = await this.client.get('/market/vol-meter');
    return response.data;
  }

  async getTimingMeter(): Promise<any> {
    const response = await this.client.get('/market/timing-meter');
    return response.data;
  }

  async getCollapseStages(): Promise<any> {
    const response = await this.client.get('/market/collapse-stages');
    return response.data;
  }

  async getMarketBreadth(): Promise<any> {
    const response = await this.client.get('/market/market-breadth');
    return response.data;
  }

  async saveTradeRecommendation(tradeId: number, action: string, notes?: string): Promise<any> {
    const response = await this.client.post(`/trades/${tradeId}/recommendation`, { action, notes });
    return response.data;
  }

  async recordTrim(tradeId: number, sharesSold: number, exitPrice: number, trimDate: string, notes?: string, commission?: number): Promise<any> {
    const response = await this.client.post(`/trades/${tradeId}/trim`, {
      shares_sold: sharesSold, exit_price: exitPrice, trim_date: trimDate, notes, commission,
    });
    return response.data;
  }
  // ── Media Trending Stocks ────────────────────────────────────────────────
  async getLatestTrendingStocks(minSources: number = 1): Promise<any> {
    const response = await this.client.get('/trending-stocks/latest', {
      params: { min_sources: minSources },
    });
    return response.data;
  }

  async getTrendingStocksForMonth(month: string): Promise<any> {
    const response = await this.client.get(`/trending-stocks/${month}`);
    return response.data;
  }

  async triggerTrendingScrape(month?: string): Promise<any> {
    const params: any = {};
    if (month) params.month = month;
    const response = await this.client.post('/trending-stocks/scrape', null, {
      params,
      timeout: 30000,
    });
    return response.data;
  }

  async getTrendingMonths(): Promise<{ months: string[] }> {
    const response = await this.client.get('/trending-stocks/months');
    return response.data;
  }

  // ── Future Stocks ──────────────────────────────────────────────────────────
  async getFutureStocksCategories(enrich: boolean = true): Promise<any> {
    const response = await this.client.get('/future-stocks/categories', {
      params: { enrich },
      timeout: 60000,
    });
    return response.data;
  }

  // ===== ADVANCED TRADING =====
  async getTurningPoints(symbol: string, lookback: string = '2 Y', atrMult = 2.0, horizon = 10, timeframe = '1d'): Promise<any> {
    const response = await this.client.get(`/advanced/turning-points/${encodeURIComponent(symbol)}`, {
      params: { lookback, atr_mult: atrMult, horizon, timeframe }, timeout: 120000,
    });
    return response.data;
  }

  async getTurningPointsHeatmap(symbol: string, timeframes: string, bars = 30): Promise<any> {
    const response = await this.client.get(`/advanced/turning-points/${encodeURIComponent(symbol)}/heatmap`, {
      params: { timeframes, bars }, timeout: 120000,
    });
    return response.data;
  }

  async getBarSubscriptions(): Promise<any> {
    const response = await this.client.get('/advanced/bars/subscriptions', { timeout: 30000 });
    return response.data;
  }

  async addBarSubscription(symbol: string, interval: string, lookbackDays: number): Promise<any> {
    const response = await this.client.post('/advanced/bars/subscriptions', null, {
      params: { symbol, interval, lookback_days: lookbackDays }, timeout: 600000,
    });
    return response.data;
  }

  async removeBarSubscription(symbol: string, interval: string): Promise<any> {
    const response = await this.client.delete('/advanced/bars/subscriptions', { params: { symbol, interval } });
    return response.data;
  }

  async getTurningPointsRace(evalBars = 126): Promise<any> {
    const response = await this.client.get('/advanced/turning-points/race', { params: { eval_bars: evalBars }, timeout: 30000 });
    return response.data;
  }

  // ===== FUTURE STOCKS LAB (experimental) =====
  async getFutureStocksLabGroups(): Promise<any> {
    const response = await this.client.get('/future-stocks/lab/groups', { timeout: 30000 });
    return response.data;
  }

  async getFutureStocksLabGroupAnalysis(groupId: string, duration: string = '1 Y', force = false): Promise<any> {
    const response = await this.client.get(`/future-stocks/lab/group/${groupId}/analysis`, {
      params: { duration, force }, timeout: 180000,
    });
    return response.data;
  }

  async getFutureStocksLabAnalysis(symbol: string, duration: string = '1 Y', force = false): Promise<any> {
    const response = await this.client.get(`/future-stocks/lab/analysis/${symbol}`, {
      params: { duration, force }, timeout: 60000,
    });
    return response.data;
  }

  async getFutureStocksLockupWatch(): Promise<any> {
    const response = await this.client.get('/future-stocks/lockup-watch', {
      timeout: 30000,
    });
    return response.data;
  }

  async refreshFutureStocks(): Promise<any> {
    const response = await this.client.post('/future-stocks/refresh', null, {
      timeout: 60000,
    });
    return response.data;
  }

  async getPreIpoCompanies(): Promise<any> {
    const response = await this.client.get('/future-stocks/pre-ipo', {
      timeout: 30000,
    });
    return response.data;
  }

  async getFutureStocksDiscovery(days: number = 14, forceRefresh: boolean = false): Promise<any> {
    const response = await this.client.get('/future-stocks/discovery', {
      params: { days, force_refresh: forceRefresh },
      timeout: 30000,
    });
    return response.data;
  }

  // ===== ISRAELI STARTUPS RADAR =====
  async getIsraeliFounders(): Promise<any> {
    const response = await this.client.get('/israeli-startups/founders', { timeout: 30000 });
    return response.data;
  }

  async getIsraeliSectors(): Promise<any> {
    const response = await this.client.get('/israeli-startups/sectors', { timeout: 30000 });
    return response.data;
  }

  async getIsraeliDiscovery(days: number = 90, forceRefresh: boolean = false): Promise<any> {
    const response = await this.client.get('/israeli-startups/discovery', {
      params: { days, force_refresh: forceRefresh },
      timeout: 30000,
    });
    return response.data;
  }

  async refreshIsraeliStartups(): Promise<any> {
    const response = await this.client.post('/israeli-startups/refresh', null, { timeout: 60000 });
    return response.data;
  }

  async getIsraeliNews(date?: string): Promise<any> {
    const response = await this.client.get('/israeli-startups/news', {
      params: date ? { date } : {},
      timeout: 30000,
    });
    return response.data;
  }

  async getIsraeliNewsSummary(date?: string, regenerate = false): Promise<any> {
    const response = await this.client.get('/israeli-startups/news/summary', {
      params: { ...(date ? { date } : {}), regenerate },
      timeout: 30000,
    });
    return response.data;
  }

  // ===== AUTH / PROFILE =====
  async getMe(): Promise<any> {
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<any> {
    const response = await this.client.post('/auth/change-password', {
      current_password: currentPassword, new_password: newPassword,
    });
    return response.data;
  }

  async logPageView(path: string, ticker?: string): Promise<void> {
    try {
      await this.client.post('/activity/page', { path, ticker: ticker || null }, { timeout: 8000 });
    } catch { /* best-effort: never block navigation on logging */ }
  }

  // ===== TOP PICKS =====
  async getTopPicks(limit = 20, forceRefresh = false): Promise<any> {
    const response = await this.client.get('/top-picks', { params: { limit, force_refresh: forceRefresh }, timeout: 30000 });
    return response.data;
  }

  async getRotationRadar(limit = 25): Promise<any> {
    const response = await this.client.get('/top-picks/rotation', { params: { limit }, timeout: 30000 });
    return response.data;
  }

  async getActionBoard(forceRefresh = false): Promise<any> {
    const response = await this.client.get('/top-picks/action-board', { params: { force_refresh: forceRefresh }, timeout: 45000 });
    return response.data;
  }

  async getMomentumLeaders(limit = 30, forceRefresh = false): Promise<any> {
    const response = await this.client.get('/top-picks/momentum', { params: { limit, force_refresh: forceRefresh }, timeout: 30000 });
    return response.data;
  }

  async getEmergingBreakouts(limit = 25, thematicOnly = true, forceRefresh = false): Promise<any> {
    const response = await this.client.get('/top-picks/emerging', { params: { limit, thematic_only: thematicOnly, force_refresh: forceRefresh }, timeout: 40000 });
    return response.data;
  }

  async getAppHealth(includeSlow = false): Promise<any> {
    const response = await this.client.get('/app-health',
      { params: { include_slow: includeSlow }, timeout: 180000 });
    return response.data;
  }

  async getMachineHealth(): Promise<any> {
    const response = await this.client.get('/app-health/machine', { timeout: 20000 });
    return response.data;
  }

  async getSimulationImprovements(refresh = false): Promise<any> {
    const response = await this.client.get('/simulation/improvements',
      { params: { refresh }, timeout: 90000 });
    return response.data;
  }

  async getCompanyProfile(symbol: string, refresh = false): Promise<any> {
    const response = await this.client.get(`/top-picks/company/${encodeURIComponent(symbol)}`,
      { params: { refresh }, timeout: 25000 });
    return response.data;
  }

  async getExitSignals(symbols?: string): Promise<any> {
    const response = await this.client.get('/top-picks/exit-signals', { params: symbols ? { symbols } : {}, timeout: 30000 });
    return response.data;
  }

  async getTopPicksDigest(topN = 10): Promise<any> {
    const response = await this.client.get('/top-picks/digest', { params: { top_n: topN }, timeout: 30000 });
    return response.data;
  }

  async getTelegramStatus(): Promise<any> {
    const response = await this.client.get('/top-picks/telegram-status');
    return response.data;
  }

  async sendTopPicksTelegram(topN = 10): Promise<any> {
    const response = await this.client.post('/top-picks/send-telegram', null, { params: { top_n: topN }, timeout: 30000 });
    return response.data;
  }

  // ===== ADMIN =====
  async adminListUsers(): Promise<any> {
    const response = await this.client.get('/admin/users');
    return response.data;
  }

  async adminCreateUser(username: string, email: string): Promise<any> {
    const response = await this.client.post('/admin/users', { username, email });
    return response.data;
  }

  async adminUpdateUser(userId: number, patch: { is_active?: boolean; is_admin?: boolean; reset_password?: boolean }): Promise<any> {
    const response = await this.client.patch(`/admin/users/${userId}`, patch);
    return response.data;
  }

  async adminDeleteUser(userId: number): Promise<any> {
    const response = await this.client.delete(`/admin/users/${userId}`);
    return response.data;
  }

  async adminSessions(): Promise<any> {
    const response = await this.client.get('/admin/sessions');
    return response.data;
  }

  async adminOverview(days = 7) {
    const response = await this.client.get('/admin/overview', { params: { days } });
    return response.data;
  }

  async adminUserUsage(userId: number, days = 7) {
    const response = await this.client.get(`/admin/users/${userId}/usage`, { params: { days } });
    return response.data;
  }

  async adminActivity(params: { user_id?: number; ticker?: string; limit?: number } = {}): Promise<any> {
    const response = await this.client.get('/admin/activity', { params });
    return response.data;
  }

  // ===== TRADE JOURNAL =====
  async listTrades(): Promise<any> {
    const response = await this.client.get('/trades');
    return response.data;
  }

  async logTrade(payload: {
    symbol: string;
    action_type: string;
    trade_date: string;
    entry_price: number;
    shares?: number;
    position_type?: string;
    notes?: string;
    exit_price?: number;
    exit_date?: string;
    entry_commission?: number;
    exit_commission?: number;
  }): Promise<any> {
    const response = await this.client.post('/trades', payload);
    return response.data;
  }

  async updateTrade(tradeId: number, payload: {
    shares?: number;
    position_type?: string;
    notes?: string;
    entry_price?: number;
    trade_date?: string;
    entry_commission?: number;
  }): Promise<any> {
    const response = await this.client.patch(`/trades/${tradeId}`, payload);
    return response.data;
  }

  async autoStrategy(tradeId: number): Promise<any> {
    const response = await this.client.post(`/trades/${tradeId}/auto-strategy`, {}, { timeout: 120000 });
    return response.data;
  }

  async recordExit(tradeId: number, exit_price: number, exit_date: string, exit_commission?: number): Promise<any> {
    const response = await this.client.patch(`/trades/${tradeId}/exit`, { exit_price, exit_date, exit_commission });
    return response.data;
  }

  async deleteTrade(tradeId: number): Promise<any> {
    await this.client.delete(`/trades/${tradeId}`);
  }

  async getTradeRetrospective(tradeId: number): Promise<any> {
    const response = await this.client.get(`/trades/${tradeId}/retrospective`, { timeout: 30000 });
    return response.data;
  }

  async getTradeAiRecommendation(tradeId: number): Promise<any> {
    const response = await this.client.get(`/trades/${tradeId}/ai-recommendation`, { timeout: 120000 });
    return response.data;
  }

  // ---- Options ----
  async analyzeOptions(symbol: string, budget = 300, side?: string): Promise<any> {
    const params: any = { budget };
    if (side) params.side = side;
    const r = await this.client.get(`/options/analyze/${symbol}`, { params });
    return r.data;
  }

  async getOptionRecommendations(symbols: string, budget = 300, side?: string): Promise<any> {
    const params: any = { symbols, budget, limit: 20 };
    if (side) params.side = side;
    const r = await this.client.get('/options/recommendations', { params });
    return r.data;
  }

  // ---- Simulation ----
  async getSimulationEquityCurve(): Promise<any> {
    const r = await this.client.get('/simulation/equity-curve', { timeout: 60000 });
    return r.data;
  }

  async getSimulation(): Promise<any> {
    const r = await this.client.get('/simulation');
    return r.data;
  }

  async runSimulation(): Promise<any> {
    const r = await this.client.post('/simulation/run');
    return r.data;
  }

  async getSimulationLog(limit = 60): Promise<any> {
    const r = await this.client.get('/simulation/daily-log', { params: { limit } });
    return r.data;
  }

  async getSimulationTrades(limit = 200): Promise<any> {
    const r = await this.client.get('/simulation/trades', { params: { limit } });
    return r.data;
  }

  async getSimulationPerformance(): Promise<any> {
    const r = await this.client.get('/simulation/performance');
    return r.data;
  }

  async getSimulationLedger(limit = 500): Promise<any> {
    const r = await this.client.get('/simulation/ledger', { params: { limit } });
    return r.data;
  }

  async reconcileSimulation(): Promise<any> {
    const r = await this.client.get('/simulation/reconcile');
    return r.data;
  }

  async getEngineFindings(): Promise<any> {
    const r = await this.client.get('/simulation/engine-findings');
    return r.data;
  }

  async resetSimulation(): Promise<any> {
    const r = await this.client.post('/simulation/reset');
    return r.data;
  }

  async scanOptions(params: any = {}): Promise<any> {
    const r = await this.client.get('/options/scan', { params });
    return r.data;
  }

  async getOptionsEvidence(): Promise<any> {
    const r = await this.client.get('/options/evidence');
    return r.data;
  }

  async optionsCostCheck(bid: number, ask: number, budget = 300, legs = 1): Promise<any> {
    const r = await this.client.get('/options/cost-check', { params: { bid, ask, budget, legs } });
    return r.data;
  }

  async getTradeLifecycles(): Promise<any> {
    const response = await this.client.get('/trades/lifecycle');
    return response.data;
  }

  // News Movers (Feature 40)
  async getNewsMovers(): Promise<any> {
    const response = await this.client.get('/news-movers/latest');
    return response.data;
  }

  async getNewsMoversTrackRecord(): Promise<any> {
    const response = await this.client.get('/news-movers/track-record');
    return response.data;
  }

  async refreshNewsMovers(): Promise<any> {
    const response = await this.client.post('/news-movers/refresh');
    return response.data;
  }

  async getHealth(): Promise<any> {
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
    const { default: axios } = await import('axios');
    const response = await axios.get(`${base}/health`, { timeout: 5000 });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
